import React, { useState } from 'react'
import moment from 'moment'
import { Drawer } from 'antd'
import { useHistory } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import pluralize from 'pluralize'

import { normalize, notifOps, summarizeProcurementInvoice, usePet, useToggle } from '../../modules'

import Datepicker from '../reusable/datePicker'
import Confirm from '../reusable/confirm'

// Modeled directly on recordPayment.js - same {invoices, supplier, onPerform} shape, same
// outstanding-capped amount idea - but with a type toggle: 'return' (physical stock actually
// goes back, tied to one invoice's specific line items with a return-quantity per line) or
// 'adjustment' (pure financial credit, no stock movement, applies across however many
// invoices are selected like a payment does). Return only makes sense against exactly one
// invoice - a return is about a specific delivery, not a blended pool of invoices.
const RecordCreditNote = ({ invoices = [], supplier, onPerform }) => {
    const [date, setDate] = useState(moment())
    const [type, setType] = useState(invoices.length === 1 ? 'return' : 'adjustment')
    const [reason, setReason] = useState()
    const [amount, setAmount] = useState(0)
    const [quantities, setQuantities] = useState({}) // { [itemIndex]: quantity }

    const { enqueueSnackbar } = useSnackbar()
    const [visible, toggleVisible] = useToggle(true)
    const history = useHistory()

    const singleInvoice = invoices.length === 1 ? invoices[0] : null
    const invoicesTotal = invoices.reduce((total, inv) => total + summarizeProcurementInvoice(inv?.items, inv?.amountPaid, inv?.amountCredited).outstanding, 0)

    const setQuantity = (index, value) => setQuantities(q => ({ ...q, [index]: Number(value) }))

    const returnItems = Object.entries(quantities)
        .filter(([, qty]) => qty > 0)
        .map(([index, qty]) => ({ invoiceItemIndex: Number(index), quantity: qty }))

    // Rough client-side estimate for display only - the backend recomputes the exact
    // proportional gross/discount/vat per line from the invoice itself.
    const estimatedReturnAmount = returnItems.reduce((total, { invoiceItemIndex, quantity }) => {
        const item = singleInvoice?.items?.[invoiceItemIndex]
        if (!item) return total
        const factor = item.singles ? item.bulkCount : item.bulkCount * item.containing
        if (!(factor > 0)) return total
        const netPerUnit = ((item.bp - item.discount) * (1 + item.vat / 100))
        return total + netPerUnit * quantity
    }, 0)

    const close = () => {
        toggleVisible()
        setTimeout(() => history.goBack(), 300)
    }

    const onComplete = (_, notif, ops) => {
        onPerform()
        notif('Credit note has been recorded', ops('success'))
        setTimeout(close, 300)
    }

    const [{ loading }, execute] = usePet('recordCreditNote', {
        supplier: supplier?._id,
        type,
        invoice: type === 'return' ? singleInvoice?._id : undefined,
        items: type === 'return' ? returnItems : undefined,
        amount: type === 'adjustment' ? amount : undefined,
        reason,
        invoiceIds: invoices.map(inv => inv._id),
    }, { notify: true, callback: onComplete, manual: true })

    const enableReason = reason?.length > 0
    const enableReturn = type === 'return' && returnItems.length > 0
    const enableAdjustment = type === 'adjustment' && amount > 0 && amount <= invoicesTotal

    const recordCreditNote = () => {
        if (!enableReason) {
            enqueueSnackbar('Please indicate a reason for this credit note', notifOps('warning'))
            return
        }

        if (type === 'return' && !enableReturn) {
            enqueueSnackbar('Please enter a return quantity for at least one item', notifOps('warning'))
            return
        }

        if (type === 'adjustment' && !enableAdjustment) {
            enqueueSnackbar('Please enter a valid amount (up to the outstanding total)', notifOps('warning'))
            return
        }

        Confirm({
            title: `Record ${type === 'return' ? 'return' : 'credit'}?`,
            message: type === 'return'
                ? `This will return the selected items to ${supplier?.name} and credit ${supplier?.name}'s account accordingly.`
                : `This will credit Ksh. ${amount} to ${supplier?.name}'s account.`,
            okText: 'Record Credit Note',
            onOk: execute,
        })
    }

    return (
        <Drawer
            closable
            maskClosable
            visible={visible}
            onClose={close}
            footer={
                <div className="w-full text-right">
                    <button onClick={ recordCreditNote } className="text-white mr-2 hisani-btn-orange font-medium rounded-lg text-sm px-5 py-2.5 text-center">Save</button>
                </div>
            }
            width="40%">
            <p className="font-extrabold text-3xl mb-0">Record Credit Note</p>
            <p className="text-sm italic mb-3 text-gray-700">Outstanding across selected invoice(s): <span className="font-semibold">Ksh. { normalize(invoicesTotal) }</span></p>

            <div className="flex mb-4">
                <button
                    onClick={ () => setType('return') }
                    disabled={ invoices.length !== 1 }
                    className={ `mr-2 px-4 py-2 rounded-lg text-sm ${ type === 'return' ? 'hisani-btn-orange text-white' : 'bg-gray-200 text-gray-700' } ${ invoices.length !== 1 ? 'opacity-50 cursor-not-allowed' : '' }` }>
                    Return
                </button>
                <button
                    onClick={ () => setType('adjustment') }
                    className={ `px-4 py-2 rounded-lg text-sm ${ type === 'adjustment' ? 'hisani-btn-orange text-white' : 'bg-gray-200 text-gray-700' }` }>
                    Adjustment
                </button>
            </div>
            { invoices.length !== 1 && <p className="text-gray-500 text-xs italic mb-3">Return only applies against a single selected invoice - select just one to return specific items.</p> }

            <div className="w-full mb-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">Date</label>
                <Datepicker value={date} onChange={setDate} />
            </div>

            {
                type === 'return' && singleInvoice &&
                <div className="w-full mb-3">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">Items to return</label>
                    <table className="table-auto w-full">
                        <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                            <tr>
                                <th className="p-2"><div className="font-semibold text-left">Item</div></th>
                                <th className="p-2"><div className="font-semibold text-left">Received</div></th>
                                <th className="p-2"><div className="font-semibold text-left">Return Qty</div></th>
                            </tr>
                        </thead>
                        <tbody className="text-sm font-medium divide-y divide-gray-100">
                            {
                                (singleInvoice.items ?? []).map((item, index) => {
                                    const factor = item.singles ? item.bulkCount : item.bulkCount * item.containing
                                    return (
                                        <tr key={ index }>
                                            <td className="p-2">{ item.name }{ item.batchNo ? ` (${item.batchNo})` : '' }</td>
                                            <td className="p-2">{ factor } { pluralize('unit', factor) }</td>
                                            <td className="p-2">
                                                <input
                                                    className="appearance-none block w-24 bg-gray-200 text-gray-700 border border-gray-200 rounded py-2 px-3 leading-tight focus:outline-none focus:bg-white"
                                                    type="number"
                                                    min={0}
                                                    max={factor}
                                                    value={ quantities[index] ?? '' }
                                                    onChange={ e => setQuantity(index, e.target.value) }
                                                    placeholder="0" />
                                            </td>
                                        </tr>
                                    )
                                })
                            }
                        </tbody>
                    </table>
                    { estimatedReturnAmount > 0 && <p className="text-gray-500 text-xs italic mt-2">Estimated credit: Ksh. { normalize(estimatedReturnAmount) }</p> }
                </div>
            }

            {
                type === 'adjustment' &&
                <div className="w-full mb-3">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">Amount</label>
                    <input
                        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                        type="number"
                        value={ amount }
                        onChange={ e => {
                            if (Number(e.target.value) > invoicesTotal) {
                                enqueueSnackbar(`Credit amount cannot be higher than the outstanding total (Ksh. ${normalize(invoicesTotal)})`, notifOps('warning'))
                                return
                            }
                            setAmount(Number(e.target.value))
                        } }
                        placeholder="2000" />
                </div>
            }

            <div className="w-full mb-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">Reason</label>
                <textarea
                    rows={4}
                    className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                    value={reason}
                    onChange={ e => setReason(e.target.value) }
                    placeholder="Why is this credit note being recorded?" />
            </div>
        </Drawer>
    )
}

export default RecordCreditNote
